import React, { useEffect, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { createUser, updateUser } from "../redux/actions/userActions";
import { getBranches } from "../redux/actions/branchActions";
import { getRoles } from "../redux/actions/roleActions";
import { cleanFormData } from "../helpers/formHelpers";
import { userActions } from "../redux/slices/user";
import { UserStatus } from "../shared/enums/UserStatus";
import { UserCreateRequestDto } from "../shared/dtos/user/UserCreateRequestDto";
import { UserUpdateRequestDto } from "../shared/dtos/user/UserUpdateRequestDto";
import { UserResponseDto } from "../shared/dtos/user/UserResponseDto";
import { UserSummaryDto } from "../shared/dtos/user/UserSummaryDto";
import { castEnumForDisplay } from "../helpers/enumHelpers";

interface UserFormValues {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  branchId: string;
  userRole: string;
  password: string;
  userStatus: UserStatus;
}

const baseValidation = {
  firstName: Yup.string().trim().required("First Name is required"),
  middleName: Yup.string().trim().nullable(),
  lastName: Yup.string().trim().required("Last Name is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
  branchId: Yup.string().required("Branch is required"),
  userRole: Yup.string().required("Role is required"),
};

const createUserSchema = Yup.object().shape({
  ...baseValidation,
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  userStatus: Yup.mixed<UserStatus>()
    .oneOf(Object.values(UserStatus), "Invalid status")
    .required("Status is required"),
});

const updateUserSchema = Yup.object().shape({
  ...baseValidation,
  userStatus: Yup.mixed<UserStatus>()
    .oneOf(Object.values(UserStatus), "Invalid status")
    .required("Status is required"),
  password: Yup.string()
    .test(
      "min-length-if-present",
      "Password must be at least 6 characters",
      (value) => {
        if (!value) return true;
        return value.length >= 6;
      },
    )
    .nullable(),
});

interface CreateOrUpdateUserFormProps {
  user?: UserResponseDto | UserSummaryDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}
const CreateOrUpdateUserForm: React.FC<CreateOrUpdateUserFormProps> = ({
  user = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: roles } = useAppSelector((state) => state.role);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (!roles.length) dispatch(getRoles());
    if (isSuperAdmin && !branches.length) dispatch(getBranches());
  }, [branches.length, dispatch, isSuperAdmin, roles.length]);

  const branchOptions = useMemo(
    () => branches.map((branch) => ({ value: branch.id, label: branch.name })),
    [branches],
  );

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: role.name,
        label: castEnumForDisplay(role.name) || role.name,
      })),
    [roles],
  );

  const statusOptions = useMemo(
    () =>
      Object.values(UserStatus).map((status) => ({
        value: status,
        label: castEnumForDisplay(status) || status,
      })),
    [],
  );

  const initialValues: UserFormValues = {
    firstName: user?.firstName || "",
    middleName: user?.middleName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    branchId: user?.branchId || (!isSuperAdmin && userInfo?.branchId) || "",
    userRole: user?.userRole || "",
    userStatus: user?.userStatus || UserStatus.ACTIVE,
    password: "",
  };

  const handleSubmit = (values: UserFormValues) => {
    const cleaned = cleanFormData(values);

    if (isEditing && user) {
      const { password, ...rest } = cleaned;
      const updatePayload: UserUpdateRequestDto = {
        ...rest,
        ...(password ? { password } : {}),
      };

      dispatch(updateUser({ id: user.id, values: updatePayload }));
      return;
    }

    dispatch(createUser(cleaned as UserCreateRequestDto));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? userActions.setErrorUpdate(null)
              : userActions.setErrorCreate(null),
          )
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEditing ? updateUserSchema : createUserSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid }: FormikProps<UserFormValues>) => (
          <Form>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isSuperAdmin ? "1fr 1fr" : "1fr",
                  },
                }}
              >
                {isSuperAdmin && (
                  <FormSelectField
                    name="branchId"
                    label="Branch"
                    options={branchOptions}
                  />
                )}
                <FormSelectField
                  name="userRole"
                  label="User Role"
                  options={roleOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                }}
              >
                <FormTextField name="firstName" label="First Name" />
                <FormTextField
                  name="middleName"
                  label="Middle Name (Optional)"
                />
                <FormTextField name="lastName" label="Last Name" />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isEditing ? "1fr 1fr" : "1fr",
                  },
                }}
              >
                <FormTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  disabled={isEditing}
                />
                {isEditing && (
                  <FormSelectField
                    name="userStatus"
                    label="User Status"
                    options={statusOptions}
                  />
                )}
              </Box>

              <FormTextField
                name="password"
                label={
                  isEditing ? "New Password (Optional)" : "Temporary Password"
                }
                type="password"
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create User"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <PersonAddOutlinedIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateUserForm;
